import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AddOutline24Icon,
  InformationFill18Icon,
  SuccessOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { postAlternativeTransportLog } from "api/alternativeTransportLogs";
import { getBackendUrl } from "api/common";
import { usePatchRequest } from "api/http/hooks";
import { PanelAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line drops/no-direct-spor-import
import { DialogContent, DialogHeader } from "@vygruppen/spor-react";
import {
  Box,
  Button,
  ButtonGroup,
  DialogBody,
  DialogActionTrigger,
  DialogFooter,
  DialogRoot,
  DialogTitle,
  HStack,
  StaticCard,
  Textarea,
  VStack,
  createToast,
  tokens,
} from "shared/components/Spor";
import { MLHeading, Text } from "shared/components/typography/TitleStyles";
import {
  AlternativeTransportLogReq,
  ReserveAlternativeTransport,
} from "shared/types/alternativeTransport";

type BookingRequestBody = {
  available: boolean;
};

/**
 * Ta i bruk / Fristill action, available for both plan- and
 * DROPS-originated backup buses. Includes both taking a bus in use and releasing it ammending the log accordingly.
 */
export const useTakeInUseOrReleaseBackupAction = (
  vehicle: ReserveAlternativeTransport,
): PanelAction => {
  const { t } = useTranslation();
  const { id, isAvailable, workShiftId } = vehicle;
  const [showModal, setShowModal] = useState(false);
  const [logMessage, setLogMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittedAction = useRef<{
    successText: string;
    releaseLogMessage: string | null;
  } | null>(null);

  const queryClient = useQueryClient();

  const {
    patchRequest,
    patchRequestStatus: openState,
    resetStatus,
  } = usePatchRequest<string, BookingRequestBody>(
    `${getBackendUrl()}/alternativeTransports/reserve/${id}/book`,
  );

  const logMutation = useMutation({
    mutationKey: ["postAlternativeTransportLogs", id],
    mutationFn: (data: AlternativeTransportLogReq) =>
      postAlternativeTransportLog(id, data),
  });
  const { mutateAsync: postLog } = logMutation;

  useEffect(() => {
    const action = submittedAction.current;
    if (!action) return;
    if (openState.status === "failure") {
      setIsSubmitting(false);
      submittedAction.current = null;
      createToast({
        variant: "error",
        text: action.releaseLogMessage
          ? t("Fristilling feilet.")
          : t("Kunne ikke ta i bruk beredskapsbussen."),
        closable: true,
        inverted: true,
      });
    }
    if (openState.status === "success") {
      submittedAction.current = null;
      createToast({
        variant: "success",
        text: action.successText,
        closable: true,
        inverted: true,
      });
      const runInvalidation = async () => {
        try {
          if (action.releaseLogMessage) {
            try {
              await postLog({
                alternativeTransportId: id,
                distributionStopPlaces: "",
                message: action.releaseLogMessage,
              });
            } catch (error) {
              log(
                LogLevel.error,
                "useTakeInUseOrReleaseBackupAction",
                "Failed to log the release of a backup bus",
                error,
              );
              createToast({
                variant: "error",
                text: t("Beredskapsbussen er fristilt, men loggføring feilet."),
                closable: true,
                inverted: true,
              });
            }
          }
          await queryClient.invalidateQueries({
            queryKey: ["reserveAltTransport"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["backupDetails"],
          });
        } finally {
          setIsSubmitting(false);
          resetStatus();
          setShowModal(false);
          setLogMessage("");
        }
      };
      void runInvalidation();
    }
  }, [openState.status, queryClient, resetStatus, postLog, id, t]);

  const closeModal = () => {
    if (isSubmitting) return;
    setShowModal(false);
    setLogMessage("");
    resetStatus();
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    submittedAction.current = {
      successText: isAvailable
        ? t("Beredskap {{workShiftId}} er i bruk.", { workShiftId })
        : t("Beredskap {{workShiftId}} er fristilt.", { workShiftId }),
      releaseLogMessage: isAvailable
        ? null
        : t("Beredskapsbussen er fristilt."),
    };
    if (isAvailable && logMessage.trim().length > 0) {
      logMutation.mutate({
        alternativeTransportId: id,
        distributionStopPlaces: "",
        message: logMessage.trim(),
      });
    }
    patchRequest({ available: !isAvailable });
  };

  const title = isAvailable
    ? t("Ta i bruk Beredskapsbuss {{workShiftId}}", { workShiftId })
    : t("Vil du fristille Beredskap {{workShiftId}}?", { workShiftId });
  const actionTitle = isAvailable ? t("Ta i bruk") : t("Ja, fristill");
  const isError = openState.status === "failure";
  const formId = `takeInUseOrRelease-${id}-form`;

  return {
    key: "takeInUseOrRelease",
    label: isAvailable ? t("Ta i bruk") : t("Fristill"),
    leftIcon: isAvailable ? <AddOutline24Icon /> : <SuccessOutline24Icon />,
    buttonVariant: isAvailable ? "primary" : "tertiary",
    buttonSize: "sm",
    onSelect: () => setShowModal(true),
    modal: showModal ? (
      <DialogRoot open onOpenChange={closeModal} size="lg" placement="center">
        <DialogContent>
          <DialogHeader>
            <MLHeading as={DialogTitle}>{title}</MLHeading>
          </DialogHeader>
          <form id={formId} onSubmit={onSubmit}>
            <DialogBody
              pb={4}
              pt={2}
              m={0}
              style={{ backgroundColor: "transparent", border: "none" }} // REVIEW-SPOR: DialogBody should be transparent and without border, and it should not affected by drops-spor overrides.
            >
              <VStack
                alignItems="stretch"
                gap={tokens.default.size.spacing[3]}
                w="100%"
              >
                {isAvailable ? (
                  <>
                    <Textarea
                      label={t("Legg til i beredskapsbuss-logg")}
                      size="sm"
                      value={logMessage}
                      onChange={(e) => setLogMessage(e.target.value)}
                      minH="10rem"
                      autoresize
                    />
                    <StaticCard
                      colorPalette="blue"
                      border="1px solid"
                      borderColor="darkBlue"
                      padding={2}
                      id="infoBoxActionModal"
                    >
                      <HStack
                        alignItems="flex-start"
                        gap={tokens.default.size.spacing[1.5]}
                      >
                        <Box flexShrink={0} display="flex">
                          <InformationFill18Icon />
                        </Box>
                        <Text>
                          {t(
                            'Bussen blir markert som "I bruk" til den fristilles av DROPS eller en kundeveileder.',
                          )}
                        </Text>
                      </HStack>
                    </StaticCard>
                  </>
                ) : (
                  <>
                    <Text secondary>
                      {t(
                        'Beredskapsbussen står som "I bruk" til den fristilles av kundeveileder på plattform.',
                      )}
                    </Text>
                    <Text secondary>
                      {t(
                        "Husk å loggføre trasé og ev. tognummer og skiftnummer som bussen settes inn for.",
                      )}
                    </Text>
                  </>
                )}
              </VStack>
            </DialogBody>
          </form>
          <DialogFooter flexDir="column" gap="12px">
            {isError && (
              <StaticCard w="100%" colorPalette="red" padding={2}>
                {isAvailable
                  ? t("Kunne ikke ta i bruk beredskapsbussen.")
                  : t("Kunne ikke fristille beredskapsbussen.")}{" "}
                {t("Prøv på nytt, eller kontakt IT hvis feilen vedvarer.")}
              </StaticCard>
            )}
            <ButtonGroup w="full" justifyContent="space-between">
              <DialogActionTrigger asChild>
                <Button type="button" variant="ghost" disabled={isSubmitting}>
                  {t("Avbryt")}
                </Button>
              </DialogActionTrigger>
              <Button
                form={formId}
                type="submit"
                variant="primary"
                loading={isSubmitting}
              >
                {isError ? t("Prøv igjen") : actionTitle}
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    ) : null,
  };
};
